import type { RadioChangeEvent } from 'antd';
import { Radio } from 'antd';
import DigitalTrapsFiltersContainer from '@features/regional-overview/digital-traps/components/digital-traps-filters/digital-traps-filters';
import InfoButton from '@features/regional-overview/digital-traps/components/info-button/info-button.component';
import TabLoading from '@features/regional-overview/digital-traps/components/tab-loading';
import { useFlightPredictions } from '@features/regional-overview/digital-traps/components/trap-details/flight-predictions/flight-predictions.hook';
import { useDigitalTrapsStore } from '@features/regional-overview/digital-traps/digital-traps.zustand';
import { DigitalTrapFilterType } from '@features/regional-overview/digital-traps/domain';
import { OBSERVED_PREDICTIONS_TOGGLE } from '@features/regional-overview/digital-traps/domain/tests-id.constants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterWrapper } from '../../trap-management-tabs.style';
import styles from './daily-counts.module.css';
import { FlightPredictionsTab } from './flight-predictions-tab.component';
import { ObservedCountsTab } from './observed-counts-tab.component';

const OBSERVED_COUNTS_FILTERS = [DigitalTrapFilterType.TEXT_SEARCH, DigitalTrapFilterType.CURRENT_DATE];
const FLIGHT_PREDICTIONS_FILTERS = [DigitalTrapFilterType.TEXT_SEARCH];

type DailyCountsView = 'observed-counts' | 'flight-predictions';

const DailyCounts = (): JSX.Element => {
  const [t] = useTranslation();
  const [activeTab, setActiveTab] = useState<DailyCountsView>('observed-counts');

  const isLoading = useDigitalTrapsStore(state => state.isLoading);
  const { anyTrapHasFlightPredictions } = useFlightPredictions();

  const toggleOptions: { label: string; value: DailyCountsView }[] = [
    { label: t('pages.regional_overview.digital_traps.daily_counts.observed_counts'), value: 'observed-counts' },
    { label: t('pages.regional_overview.digital_traps.daily_counts.flight_predictions'), value: 'flight-predictions' }
  ];

  const currentFilters = activeTab === 'observed-counts' ? OBSERVED_COUNTS_FILTERS : FLIGHT_PREDICTIONS_FILTERS;

  if (isLoading) {
    const loadingText =
      activeTab === 'observed-counts'
        ? t('pages.regional_overview.digital_traps.daily_counts.loading_text')
        : t('pages.regional_overview.digital_traps.daily_counts.fligh_prediction_loading_text');
    return <TabLoading text={loadingText} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {anyTrapHasFlightPredictions && (
          <div className={styles.toggleRow}>
            <Radio.Group
              className={styles.toggle}
              options={toggleOptions}
              value={activeTab}
              onChange={(e: RadioChangeEvent) => setActiveTab(e.target.value as DailyCountsView)}
              optionType='button'
              buttonStyle='solid'
              data-testid={OBSERVED_PREDICTIONS_TOGGLE}
            />
            <InfoButton />
          </div>
        )}
        <FilterWrapper>
          <DigitalTrapsFiltersContainer enabledFilters={currentFilters} context='Daily Counts' />
        </FilterWrapper>
      </div>

      {activeTab === 'observed-counts' ? <ObservedCountsTab /> : <FlightPredictionsTab />}
    </div>
  );
};

export default DailyCounts;
